import Axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { PDFDocument } from 'pdf-lib';

import { parseArrayBuffer } from '@/core/lib/ArrayBufferUtils';
import { getConfig } from '@/core/lib/Config';
import { AuthenticationError, getAuthentication } from '@/plugins/authentication/lib/Authentication';

export const ALPTIS_API_KEY = 'X-Alptis-Api-Key';

interface ApiResponseMessages {
  message: string;
  details: [
    {
      message: string;
      champ: string | null;
    },
  ];
}

const isApiResponseMessages = (raw: unknown): raw is ApiResponseMessages => {
  return !!raw && typeof raw === 'object' && Object.hasOwn(raw, 'message') && Object.hasOwn(raw, 'details');
};

export class ApiError<T = unknown> extends Error {
  #statusCode: number;

  #responseRaw?: T;

  constructor(message: string, statusCode: number, responseRaw?: T) {
    super(message);

    this.#statusCode = statusCode;
    this.#responseRaw = responseRaw;
  }

  get statusCode(): number {
    return this.#statusCode;
  }

  get responseRaw(): T | undefined {
    return this.#responseRaw;
  }

  #mapResponseMessages(): ApiResponseMessages | undefined {
    const responseRaw =
      this.#responseRaw instanceof ArrayBuffer ? parseArrayBuffer(this.#responseRaw) : this.#responseRaw;

    return isApiResponseMessages(responseRaw) ? responseRaw : undefined;
  }

  get responseMessages(): ApiResponseMessages | undefined {
    return this.#mapResponseMessages();
  }

  get firstDetail(): string | undefined {
    const responseMessages = this.#mapResponseMessages();

    return responseMessages?.details.length ? responseMessages.details[0].message : undefined;
  }
}

function convertAxiosErrorToApiError(maybeAxiosError: unknown): ApiError | undefined {
  if (maybeAxiosError && Axios.isAxiosError(maybeAxiosError) && maybeAxiosError.response) {
    return new ApiError(maybeAxiosError.message, maybeAxiosError.response.status, maybeAxiosError.response.data);
  }

  return undefined;
}

function handleErrorOnReject(axios: AxiosInstance): void {
  axios.interceptors.response.use(
    response => response,
    (error: Error) => Promise.reject(convertAxiosErrorToApiError(error) ?? error),
  );
}

async function useAuthBearerToken(axios: AxiosInstance): Promise<void> {
  const { updateToken, getState } = await getAuthentication();

  axios.interceptors.request.use(async config => {
    await updateToken();
    const state = getState();

    if (state.token === undefined) {
      throw new AuthenticationError('Authentication token is undefined');
    }

    config.headers.set('Authorization', `Bearer ${state.token}`);

    return config;
  });
}

function useApiKey(axios: AxiosInstance, keyValue: string): void {
  axios.interceptors.request.use(config => {
    config.headers.set(ALPTIS_API_KEY, keyValue);

    return config;
  });
}

export async function getEspaceDistributeurServicesApi(requiresAuth = true): Promise<AxiosInstance> {
  const {
    apiAlptis: { baseUrl, serviceEspaceDistributeur, apiKey },
  } = await getConfig();

  const axios = Axios.create({ baseURL: `${baseUrl}/${serviceEspaceDistributeur}` });

  if (requiresAuth) {
    await useAuthBearerToken(axios);
  } else {
    useApiKey(axios, apiKey);
  }

  handleErrorOnReject(axios);

  return axios;
}

export async function getEspaceDistributeurApi(): Promise<AxiosInstance> {
  const { espaceDigitalHomeUrl } = await getConfig();

  const axios = Axios.create({ baseURL: espaceDigitalHomeUrl });

  await useAuthBearerToken(axios);

  return axios;
}

export async function getEvcApi(): Promise<AxiosInstance> {
  const { evcApiBaseUrl } = await getConfig();

  const axios = Axios.create({ baseURL: evcApiBaseUrl });

  await useAuthBearerToken(axios);

  return axios;
}

export function parseTotalFromContentRange(contentRangeString: string | undefined): number {
  if (!contentRangeString) {
    return 0;
  }

  const total = parseInt(contentRangeString.substring(contentRangeString.indexOf('/') + 1));

  return isNaN(total) ? 0 : total;
}

export function assembleRangeHeader(rangeStart: number, rangeEnd: number, unit = 'items'): string {
  return `${unit}=${rangeStart.toString()}-${rangeEnd.toString()}`;
}

export function getRangePartsForPage(page: number, itemsPerPage: number): { rangeStart: number; rangeEnd: number } {
  return {
    rangeStart: (page * itemsPerPage) + 1,
    rangeEnd: (page + 1) * itemsPerPage,
  };
}

export function assembleRangeHeaderForPage(page: number, itemsPerPage: number, unit?: string): string {
  const { rangeStart, rangeEnd } = getRangePartsForPage(page, itemsPerPage);

  return assembleRangeHeader(rangeStart, rangeEnd, unit);
}

export function parseFilenameFromContentDisposition(contentDisposition: string | undefined): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  const matches = Array.from(contentDisposition.matchAll(/filename\*?\s*=\s*"?(?:utf-8'')?([^";]+)"?/gi));

  // Priority to the utf-8 match
  const utf8Match = matches.find(([source]) => /utf-8/i.exec(source));

  if (utf8Match !== undefined) {
    return decodeURIComponent(utf8Match[1]);
  }

  // Otherwise the first match
  const firstMatch = matches.find(([, match]) => !!match);

  if (firstMatch !== undefined) {
    return firstMatch[1];
  }

  return undefined;
}

export async function getFileFromResponseOrFail(
  response: AxiosResponse<ArrayBuffer | undefined, unknown>,
  forcedFileName?: string,
): Promise<File> {
  const getFileContent = (): ArrayBuffer => {
    if (!response.data) {
      throw new Error('No file content.');
    }

    return response.data;
  };

  const getFileType = (): string => {
    const fileType = response.headers['content-type'] as string | undefined;

    if (!fileType) {
      throw new Error('File type not found in Content-Type header.');
    }

    return fileType;
  };

  const getFileName = (): string => {
    if (!forcedFileName) {
      const fileName = parseFilenameFromContentDisposition(
        response.headers['content-disposition'] as string | undefined,
      );

      if (!fileName) {
        throw new Error('File name not found in Content-Disposition header.');
      }

      return fileName;
    } else {
      return forcedFileName;
    }
  };

  const fileType = getFileType();
  const fileName = getFileName();
  const fileContent = getFileContent();

  const getBlobParts = async (): Promise<BlobPart[]> => {
    if (fileType.includes('application/pdf')) {
      // Sync pdf filename with pdf metadata title if empty
      const pdfDocument = await PDFDocument.load(fileContent);

      if (!pdfDocument.getTitle()) {
        pdfDocument.setTitle(fileName);

        return [await pdfDocument.save()];
      }
    }

    return [fileContent];
  };

  return new File(await getBlobParts(), fileName, { type: fileType });
}
